import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './About.css';
import { 
  BsArrowRight, 
  BsPlayFill, 
  BsLightbulb, 
  BsCodeSlash, 
  BsGraphUpArrow, 
  BsStarFill, 
  BsFileEarmarkCheck, 
  BsPeopleFill, 
  BsRocketTakeoff, 
  BsShieldCheck, 
  BsAward, 
  BsBullseye, 
  BsLinkedin, 
  BsTwitterX,
  BsX,
  BsCheckCircleFill,
  BsCompass,
  BsEye
} from 'react-icons/bs';
import { useCMS } from '../../context/CMSContext';

/* ── Helper: Format member image URL reliably ── */
const getMemberImageUrl = (img) => {
  if (!img) return "/assets/testominals/1.jpg";
  if (img.startsWith("http://") || img.startsWith("https://") || img.startsWith("data:")) {
    return img;
  }
  if (img.startsWith("/assets/") || img.startsWith("assets/")) {
    return img.startsWith("/") ? img : `/${img}`;
  }
  const baseUrl = window.API_URL || "http://localhost:8000";
  const clean = img.replace(/^\/+/, "");
  if (clean.startsWith("uploads/")) {
    return `${baseUrl}/${clean}`;
  }
  return `${baseUrl}/uploads/${clean}`;
};

/* ── Company Milestones ── */
const milestones = [
  {
    year: "2020",
    title: "The Beginning",
    desc: "Turned our vision into action."
  },
  {
    year: "2021",
    title: "First Clients",
    desc: "Delivered our first successful projects."
  },
  {
    year: "2022",
    title: "Team Expansion",
    desc: "Welcomed a growing team of experts."
  },
  {
    year: "2023",
    title: "Service Growth",
    desc: "Expanded into AI & digital marketing."
  },
  {
    year: "2024",
    title: "Global Reach",
    desc: "Started working with international clients."
  },
  {
    year: "2025",
    title: "Bigger Impact",
    desc: "Continuing to build a better digital tomorrow."
  }
];

const About = () => {
  const [dbTeam, setDbTeam] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const { getSection } = useCMS();

  const aboutCMS = getSection('about', {
    heroEyebrow: "ABOUT US",
    heroTitleLine1: "A Team",
    heroTitleLine2: "Behind Your",
    heroHighlight: "Digital Growth",
    heroSubtitle: "We are creators, developers, marketers and AI enthusiasts on a mission to help businesses grow in the digital world.",
    missionTitle: "Our Mission",
    missionDesc: "To empower businesses with innovative digital solutions, combining creativity, technology and strategy to create meaningful growth and real-world impact.",
    visionTitle: "Our Vision",
    visionDesc: "To be a global digital community where businesses, ideas and people grow together — leveraging technology and AI to build a smarter, more opportunity-driven world.",
    milestones: milestones
  });

  const activeMilestones = (Array.isArray(aboutCMS.milestones) && aboutCMS.milestones.length > 0)
    ? aboutCMS.milestones 
    : milestones;

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic'
    });

    const fetchTeam = async () => {
      try {
        const baseUrl = window.API_URL || "http://localhost:8000";
        const res = await axios.get(`${baseUrl}/team`);
        if (Array.isArray(res.data)) {
          setDbTeam(res.data);
        } else {
          setDbTeam([]);
        }
      } catch (err) {
        console.error("Error fetching team:", err);
        setDbTeam([]);
      } finally {
        setLoadingTeam(false);
      }
    };

    fetchTeam();
  }, []);

  const scrollToStory = () => {
    const el = document.getElementById("our-story");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="about-page w-full overflow-x-hidden">

      {/* =========================================================================
          1. HERO SECTION (Dark Modern Aesthetic matching design)
          ========================================================================= */}
      <section className="relative bg-[#070b14] text-white pt-24 pb-20 lg:pt-32 lg:pb-28 overflow-hidden border-b border-slate-800/80">
        
        {/* Glow Spheres */}
        <div className="hero-glow-sphere -top-24 -left-24 opacity-60"></div>
        <div className="hero-glow-sphere top-1/2 right-0 opacity-40"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
            
            {/* Left Hero Content */}
            <div className="lg:col-span-6 space-y-6" data-aos="fade-right">
              
              {/* Eyebrow */}
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-extrabold text-[#f06600]">
                <span className="w-2 h-2 rounded-full bg-[#f06600] animate-ping inline-block"></span>
                <span>{aboutCMS.heroEyebrow || "ABOUT US"}</span>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.15] text-white">
                {aboutCMS.heroTitleLine1 || "A Team"} <br />
                {aboutCMS.heroTitleLine2 || "Behind Your"} <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-500 to-[#f06600]">
                  {aboutCMS.heroHighlight || "Digital Growth"}
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-slate-300 text-base sm:text-lg max-w-xl font-normal leading-relaxed">
                {aboutCMS.heroSubtitle || "We are creators, developers, marketers and AI enthusiasts on a mission to help businesses grow in the digital world."}
              </p>

              {/* Buttons */}
              <div className="flex flex-wrap items-center gap-4 pt-2">
                <button
                  onClick={scrollToStory}
                  className="bg-[#f06600] hover:bg-orange-600 text-white font-bold px-7 py-3.5 rounded-full shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300 flex items-center gap-2.5 text-sm sm:text-base cursor-pointer"
                >
                  <span>Our Story</span>
                  <BsArrowRight className="text-base" />
                </button>

                <button
                  onClick={() => setVideoModalOpen(true)}
                  className="glass-badge-dark hover:bg-slate-800/80 text-white font-semibold px-6 py-3.5 rounded-full border border-slate-700/80 transition-all duration-300 flex items-center gap-3 text-sm sm:text-base cursor-pointer group"
                >
                  <span className="w-8 h-8 rounded-full bg-white/10 group-hover:bg-[#f06600] flex items-center justify-center text-white transition-colors duration-300">
                    <BsPlayFill className="text-xl ml-0.5" />
                  </span>
                  <span>Watch Video</span>
                </button>
              </div>

              {/* 3 Value Pillars at Bottom */}
              <div className="pt-8 border-t border-slate-800/80 grid grid-cols-3 gap-4 text-xs sm:text-sm text-slate-300">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 text-[#f06600] flex items-center justify-center shrink-0">
                    <BsLightbulb className="text-base" />
                  </div>
                  <div>
                    <div className="font-bold text-white">Ideas</div>
                    <div className="text-slate-400 text-xs">People</div>
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 text-[#f06600] flex items-center justify-center shrink-0">
                    <BsCodeSlash className="text-base" />
                  </div>
                  <div>
                    <div className="font-bold text-white">Technology</div>
                    <div className="text-slate-400 text-xs">Solutions</div>
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 text-[#f06600] flex items-center justify-center shrink-0">
                    <BsGraphUpArrow className="text-base" />
                  </div>
                  <div>
                    <div className="font-bold text-white">Growth</div>
                    <div className="text-slate-400 text-xs">Together</div>
                  </div>
                </div>
              </div>

            </div>

            {/* Right Hero Visual Banner - Founders in Meeting Room with "bc" Logo */}
            <div className="lg:col-span-6 relative" data-aos="fade-left">
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden border border-slate-800 shadow-2xl bg-slate-900 group">
                <img 
                  src="/assets/about/founders_meeting_hero.jpg" 
                  alt="Beta Communes Founders in Meeting Room"
                  className="w-full aspect-[16/10] sm:aspect-[16/10] lg:aspect-auto lg:h-[480px] object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/assets/about/hero_team.jpg";
                  }}
                />

                {/* Subtle vignette gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#070b14]/90 via-transparent to-[#070b14]/30 pointer-events-none"></div>

                {/* Top-Right Badge: "bc Build Grow Together" */}
                <div className="absolute top-3 right-3 sm:top-5 sm:right-5 glass-badge-dark px-2.5 py-1.5 sm:px-4 sm:py-2.5 rounded-xl sm:rounded-2xl flex items-center gap-2 sm:gap-2.5 text-[10px] sm:text-xs text-white border border-white/10 shadow-xl">
                  <div className="w-5 h-5 sm:w-7 sm:h-7 rounded sm:rounded-lg bg-[#f06600] text-white flex items-center justify-center font-black text-[9px] sm:text-xs shadow-md">
                    bc
                  </div>
                  <div className="leading-tight">
                    <span className="font-bold text-white block text-[10px] sm:text-xs">Build · Grow</span>
                    <span className="text-slate-400 text-[8px] sm:text-[11px]">Together</span>
                  </div>
                </div>

                {/* Bottom-Right Floating Dark Card */}
                <div className="absolute bottom-3 right-3 sm:bottom-6 sm:right-6 max-w-[150px] sm:max-w-[210px] glass-badge-dark p-2.5 sm:p-4 rounded-xl sm:rounded-2xl border border-white/15 shadow-2xl backdrop-blur-md">
                  <div className="text-[9px] sm:text-xs uppercase tracking-wider text-[#f06600] font-extrabold mb-0.5 sm:mb-1">
                    More Than Websites
                  </div>
                  <div className="text-[11px] sm:text-sm font-bold text-white leading-snug">
                    We Build <br /> Opportunities
                  </div>
                </div>

                {/* Bottom-Left Leadership Indicator */}
                <div className="absolute bottom-3 left-3 sm:bottom-6 sm:left-6 hidden sm:flex items-center gap-3 glass-badge-dark px-3.5 py-2 rounded-xl border border-white/10 shadow-xl backdrop-blur-md">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#f06600] animate-pulse"></div>
                  <div className="text-xs text-slate-300">
                    <span className="font-bold text-white block">Executive Leadership</span>
                    <span className="text-slate-400 text-[11px]">CEO &amp; Co-Founders Strategy</span>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>


      {/* =========================================================================
          2. TRUSTED BY BUSINESSES WORLDWIDE (Partner Logos)
          ========================================================================= */}
      <section className="bg-[#f8f9fc] border-b border-slate-200/80 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[11px] sm:text-xs font-bold uppercase tracking-widest text-slate-500 mb-6">
            TRUSTED BY BUSINESSES WORLDWIDE
          </p>

          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12 lg:gap-16 opacity-70 grayscale hover:grayscale-0 transition-all duration-300">
            {/* Shopify */}
            <div className="flex items-center gap-2 font-bold text-slate-700 text-lg hover:text-emerald-600 transition-colors">
              <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                <path d="M19.78 6.55c-.06-.39-.4-.68-.8-.68l-3.35-.12-1.78-3.52c-.17-.34-.52-.55-.9-.55s-.73.21-.9.55l-1.8 3.52-3.33.12c-.4 0-.74.29-.8.68L5.3 18.28c-.04.28.06.56.26.75.2.19.48.29.76.26l12.44-1.24c.28-.03.52-.19.66-.43.14-.24.15-.54.04-.8L19.78 6.55zM12 4.25l1.37 2.71c.09.18.27.3.47.31l2.58.09-1.87 1.83c-.15.14-.21.35-.17.55l.44 2.57-2.31-1.21c-.18-.09-.39-.09-.57 0l-2.31 1.21.44-2.57c.04-.2-.02-.41-.17-.55L7.58 7.36l2.58-.09c.2-.01.38-.13.47-.31L12 4.25z"/>
              </svg>
              <span>shopify</span>
            </div>

            {/* Google */}
            <div className="flex items-center gap-1.5 font-bold text-slate-700 text-lg hover:text-blue-600 transition-colors">
              <span className="text-blue-500 font-black">G</span>
              <span className="text-red-500 font-black">o</span>
              <span className="text-yellow-500 font-black">o</span>
              <span className="text-blue-500 font-black">g</span>
              <span className="text-green-500 font-black">l</span>
              <span className="text-red-500 font-black">e</span>
            </div>

            {/* Meta */}
            <div className="flex items-center gap-2 font-bold text-slate-700 text-lg hover:text-blue-500 transition-colors">
              <svg className="w-6 h-6 fill-current text-blue-600" viewBox="0 0 24 24">
                <path d="M12 8.5C9.7 4.7 6.6 3 4.2 3 1.9 3 0 5 0 8.5c0 4.6 3.4 8.7 6.5 8.7 2.4 0 4.2-1.9 5.5-4.4 1.3 2.5 3.1 4.4 5.5 4.4 3.1 0 6.5-4.1 6.5-8.7C24 5 22.1 3 19.8 3c-2.4 0-5.5 1.7-7.8 5.5zm-5.5 6.7c-2.1 0-4.5-3.1-4.5-6.7 0-2.4 1.2-3.5 2.2-3.5 1.7 0 4 1.8 5.6 5.5-1 2.9-2.2 4.7-3.3 4.7zm11 0c-1.1 0-2.3-1.8-3.3-4.7 1.6-3.7 3.9-5.5 5.6-5.5 1 0 2.2 1.1 2.2 3.5 0 3.6-2.4 6.7-4.5 6.7z"/>
              </svg>
              <span>Meta</span>
            </div>

            {/* Figma */}
            <div className="flex items-center gap-2 font-bold text-slate-700 text-lg hover:text-purple-600 transition-colors">
              <span className="font-extrabold tracking-tight">Figma</span>
            </div>

            {/* WordPress */}
            <div className="flex items-center gap-2 font-bold text-slate-700 text-lg hover:text-sky-700 transition-colors">
              <span className="w-5 h-5 rounded-full border-2 border-current flex items-center justify-center text-xs font-black">W</span>
              <span>WORDPRESS</span>
            </div>

            {/* Laravel */}
            <div className="flex items-center gap-2 font-bold text-slate-700 text-lg hover:text-red-600 transition-colors">
              <span className="font-extrabold tracking-tight">Laravel</span>
            </div>
          </div>
        </div>
      </section>


      {/* =========================================================================
          3. OUR STORY SECTION (Light Clean Aesthetic)
          ========================================================================= */}
      <section id="our-story" className="py-20 lg:py-28 bg-white text-slate-900 border-b border-slate-100 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-12 items-center">
            
            {/* Story Text Left */}
            <div className="lg:col-span-6 space-y-6" data-aos="fade-right">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-extrabold text-[#f06600]">
                <span className="w-2 h-2 rounded-full bg-[#f06600] inline-block"></span>
                <span>OUR STORY</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900 leading-[1.2]">
                From an Idea to <br />
                <span className="text-[#f06600]">a Growing Community</span>
              </h2>

              <p className="text-slate-600 text-base sm:text-lg leading-relaxed font-normal">
                Beta Communes started with a simple idea — to make high-quality digital solutions accessible to every business. What began as a small team of tech enthusiasts has now grown into a full-service digital company helping brands across the world build, market and scale with technology.
              </p>

              <div className="pt-2">
                <Link
                  to="/portfolio"
                  className="inline-flex items-center gap-2 font-bold text-[#f06600] hover:text-orange-700 transition-colors group text-base"
                >
                  <span>Read Our Full Story</span>
                  <BsArrowRight className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>

            {/* Story Video Preview Card Right */}
            <div className="lg:col-span-6 relative" data-aos="fade-left">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-slate-950 border border-slate-200/80 group">
                <img 
                  src="/assets/about/story_meeting.jpg" 
                  alt="Beta Communes Conference Room Story" 
                  className="w-full h-[320px] sm:h-[380px] object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/assets/hero_dev_workspace.jpg";
                  }}
                />
                
                {/* Overlay Vignette */}
                <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-[1px]"></div>

                {/* Glowing Text in preview */}
                <div className="absolute top-6 left-6 text-xs font-semibold text-white/90 tracking-wide space-y-1">
                  <div className="text-orange-400 font-extrabold text-sm mb-1">Ideas · Technology</div>
                  <div>People · Impact</div>
                </div>

                {/* Central Play Button */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <button
                    onClick={() => setVideoModalOpen(true)}
                    className="w-16 h-16 rounded-full bg-[#f06600] text-white flex items-center justify-center shadow-xl play-btn-pulse hover:scale-110 transition-all cursor-pointer"
                  >
                    <BsPlayFill className="text-3xl ml-1" />
                  </button>
                  <span className="text-xs font-bold text-white tracking-wider mt-3 drop-shadow">
                    Play Our Story
                  </span>
                </div>

                {/* Floating Side Card */}
                <div className="absolute top-6 right-6 hidden sm:flex flex-col gap-2 glass-badge-light p-3.5 rounded-2xl shadow-xl text-xs font-semibold text-slate-800">
                  <div className="flex items-center gap-2">
                    <span className="text-orange-500">🎯</span>
                    <span>Started with a Vision</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-orange-500">💡</span>
                    <span>Built with Passion</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-orange-500">🚀</span>
                    <span>Growing Together</span>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>


      {/* =========================================================================
          4. STATS COUNTER BAR (4 Cards)
          ========================================================================= */}
      <section className="py-14 bg-[#fbfcfe] border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            
            {/* Stat 1 */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200/70 shadow-sm text-center hover:shadow-md transition-shadow" data-aos="fade-up" data-aos-delay="0">
              <div className="w-12 h-12 rounded-xl bg-orange-50 text-[#f06600] mx-auto flex items-center justify-center text-xl mb-3">
                <BsPeopleFill />
              </div>
              <div className="text-3xl sm:text-4xl font-black text-[#f06600]">100+</div>
              <div className="text-xs sm:text-sm font-semibold text-slate-600 mt-1">Happy Clients</div>
            </div>

            {/* Stat 2 */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200/70 shadow-sm text-center hover:shadow-md transition-shadow" data-aos="fade-up" data-aos-delay="100">
              <div className="w-12 h-12 rounded-xl bg-orange-50 text-[#f06600] mx-auto flex items-center justify-center text-xl mb-3">
                <BsFileEarmarkCheck />
              </div>
              <div className="text-3xl sm:text-4xl font-black text-[#f06600]">250+</div>
              <div className="text-xs sm:text-sm font-semibold text-slate-600 mt-1">Projects Delivered</div>
            </div>

            {/* Stat 3 */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200/70 shadow-sm text-center hover:shadow-md transition-shadow" data-aos="fade-up" data-aos-delay="200">
              <div className="w-12 h-12 rounded-xl bg-orange-50 text-[#f06600] mx-auto flex items-center justify-center text-xl mb-3">
                <BsStarFill />
              </div>
              <div className="text-3xl sm:text-4xl font-black text-[#f06600]">5+</div>
              <div className="text-xs sm:text-sm font-semibold text-slate-600 mt-1">Years of Experience</div>
            </div>

            {/* Stat 4 */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200/70 shadow-sm text-center hover:shadow-md transition-shadow" data-aos="fade-up" data-aos-delay="300">
              <div className="w-12 h-12 rounded-xl bg-orange-50 text-[#f06600] mx-auto flex items-center justify-center text-xl mb-3">
                <BsRocketTakeoff />
              </div>
              <div className="text-3xl sm:text-4xl font-black text-[#f06600]">99%</div>
              <div className="text-xs sm:text-sm font-semibold text-slate-600 mt-1">Client Satisfaction</div>
            </div>

          </div>
        </div>
      </section>


      {/* =========================================================================
          5. OUR PURPOSE / MISSION & VISION SECTION
          ========================================================================= */}
      <section className="py-20 lg:py-28 bg-white border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-14 gap-4" data-aos="fade-up">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-extrabold text-[#f06600] mb-2">
                <span className="w-2 h-2 rounded-full bg-[#f06600] inline-block"></span>
                <span>OUR PURPOSE</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900">
                Our Mission &amp; <span className="text-[#f06600]">Vision</span>
              </h2>
            </div>
            <p className="text-slate-600 text-sm sm:text-base max-w-md font-normal leading-relaxed">
              We are driven by a clear purpose and a bold vision to create a better, more connected digital future.
            </p>
          </div>

          {/* Mission & Vision Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Mission Card */}
            <div 
              className="relative rounded-3xl overflow-hidden min-h-[320px] sm:min-h-[360px] p-8 sm:p-10 flex flex-col justify-end text-white shadow-xl group"
              data-aos="fade-up"
              data-aos-delay="0"
            >
              <img 
                src="/assets/about/mission_summit.jpg" 
                alt="Our Mission Summit"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/assets/mission.png";
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/70 to-slate-950/30"></div>

              <div className="relative z-10 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#f06600] text-white flex items-center justify-center text-lg shadow-lg">
                    <BsBullseye />
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-black text-white">
                    {aboutCMS.missionTitle || "Our Mission"}
                  </h3>
                </div>
                <p className="text-slate-200 text-sm sm:text-base leading-relaxed max-w-lg">
                  {aboutCMS.missionDesc || "To empower businesses with innovative digital solutions, combining creativity, technology and strategy to create meaningful growth and real-world impact."}
                </p>
              </div>
            </div>

            {/* Vision Card */}
            <div 
              className="relative rounded-3xl overflow-hidden min-h-[320px] sm:min-h-[360px] p-8 sm:p-10 flex flex-col justify-end text-white shadow-xl group"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <img 
                src="/assets/about/vision_road.jpg" 
                alt="Our Vision Horizon"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/assets/banner.png";
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/65 to-slate-950/25"></div>

              <div className="relative z-10 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#f06600] text-white flex items-center justify-center text-lg shadow-lg">
                    <BsEye />
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-black text-white">
                    {aboutCMS.visionTitle || "Our Vision"}
                  </h3>
                </div>
                <p className="text-slate-200 text-sm sm:text-base leading-relaxed max-w-lg">
                  {aboutCMS.visionDesc || "To be a global digital community where businesses, ideas and people grow together — leveraging technology and AI to build a smarter, more opportunity-driven world."}
                </p>
              </div>
            </div>

          </div>

        </div>
      </section>


      {/* =========================================================================
          6. THE PRINCIPLES THAT GUIDE US (VALUES)
          ========================================================================= */}
      <section className="py-20 lg:py-28 bg-[#fbfcfe] border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-14 gap-4" data-aos="fade-up">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-extrabold text-[#f06600] mb-2">
                <span className="w-2 h-2 rounded-full bg-[#f06600] inline-block"></span>
                <span>OUR VALUES</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900">
                The Principles <br />
                That <span className="text-[#f06600]">Guide Us</span>
              </h2>
            </div>
            <p className="text-slate-600 text-sm sm:text-base max-w-md font-normal leading-relaxed">
              These core values shape our culture, our work, and the relationships we build with our clients, partners, and community.
            </p>
          </div>

          {/* 5 Value Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            
            {/* Innovation */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300" data-aos="fade-up" data-aos-delay="0">
              <div className="w-12 h-12 rounded-xl bg-orange-50 text-[#f06600] flex items-center justify-center text-xl mb-4">
                <BsLightbulb />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Innovation</h3>
              <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
                We embrace new ideas and emerging technologies.
              </p>
            </div>

            {/* Integrity */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300" data-aos="fade-up" data-aos-delay="80">
              <div className="w-12 h-12 rounded-xl bg-orange-50 text-[#f06600] flex items-center justify-center text-xl mb-4">
                <BsShieldCheck />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Integrity</h3>
              <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
                We believe in transparency and honesty.
              </p>
            </div>

            {/* Collaboration */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300" data-aos="fade-up" data-aos-delay="160">
              <div className="w-12 h-12 rounded-xl bg-orange-50 text-[#f06600] flex items-center justify-center text-xl mb-4">
                <BsPeopleFill />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Collaboration</h3>
              <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
                We grow together with our clients and team.
              </p>
            </div>

            {/* Excellence */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300" data-aos="fade-up" data-aos-delay="240">
              <div className="w-12 h-12 rounded-xl bg-orange-50 text-[#f06600] flex items-center justify-center text-xl mb-4">
                <BsAward />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Excellence</h3>
              <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
                We are committed to delivering the highest quality.
              </p>
            </div>

            {/* Impact */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300" data-aos="fade-up" data-aos-delay="320">
              <div className="w-12 h-12 rounded-xl bg-orange-50 text-[#f06600] flex items-center justify-center text-xl mb-4">
                <BsBullseye />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Impact</h3>
              <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">
                We focus on creating real business value.
              </p>
            </div>

          </div>

        </div>
      </section>


      {/* =========================================================================
          7. MEET OUR TEAM / THE PEOPLE BEHIND BETA COMMUNES (Dark Theme)
          ========================================================================= */}
      <section id="our-team" className="py-24 bg-[#050811] text-white relative border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-14 gap-6" data-aos="fade-up">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-extrabold text-[#f06600] mb-2">
                <span className="w-2 h-2 rounded-full bg-[#f06600] inline-block"></span>
                <span>MEET OUR TEAM</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white">
                The People <br className="hidden sm:block" />
                Behind <span className="text-[#f06600]">Beta Communes</span>
              </h2>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <p className="text-slate-400 text-xs sm:text-sm max-w-sm leading-relaxed font-normal">
                A diverse team of thinkers, creators, developers and strategists working together to turn ideas into impact.
              </p>
              <Link 
                to="/contactus"
                className="glass-badge-dark hover:bg-slate-800 text-white text-xs font-bold px-5 py-2.5 rounded-full border border-slate-700/80 transition-all flex items-center gap-2 self-start sm:self-auto shrink-0"
              >
                <span>View All Team</span>
                <BsArrowRight />
              </Link>
            </div>
          </div>

          {/* Team Grid: Only Admin-Added Team Members */}
          {loadingTeam ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="bg-[#0d1424] border border-slate-800/60 rounded-2xl p-4 animate-pulse">
                  <div className="h-60 sm:h-64 bg-slate-800/50 rounded-xl mb-4"></div>
                  <div className="h-4 bg-slate-800/80 rounded w-2/3 mb-2"></div>
                  <div className="h-3 bg-slate-800/50 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : dbTeam.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {dbTeam.map((member, index) => (
                <div
                  key={member._id || index}
                  className="team-member-card bg-[#0d1424] border border-slate-800 hover:border-[#f06600]/40 rounded-2xl overflow-hidden flex flex-col justify-between transition-all duration-300 hover:-translate-y-1 group shadow-lg shadow-black/30"
                  data-aos="fade-up"
                  data-aos-delay={(index % 4) * 80}
                >
                  {/* Member Portrait */}
                  <div className="h-64 sm:h-72 w-full overflow-hidden bg-slate-950 relative">
                    <img
                      src={getMemberImageUrl(member.image)}
                      alt={member.name}
                      loading="lazy"
                      className="member-photo w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/assets/testominals/1.jpg";
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0d1424] via-[#0d1424]/20 to-transparent opacity-90"></div>
                  </div>

                  {/* Member Details */}
                  <div className="p-5 pt-2 flex flex-col justify-between flex-grow">
                    <div>
                      <h3 className="text-base font-extrabold text-white tracking-tight leading-snug">
                        {member.name}
                      </h3>
                      <p className="text-xs font-semibold text-[#f06600] mt-1">
                        {member.profession}
                      </p>
                      {member.skill && (
                        <p className="text-[11px] text-slate-400 mt-1.5 line-clamp-2 leading-relaxed">
                          {member.skill}
                        </p>
                      )}
                      {member.description && (
                        <p className="text-[11px] text-slate-400/80 mt-1 line-clamp-2 leading-relaxed">
                          {member.description}
                        </p>
                      )}
                    </div>

                    {/* Social Buttons */}
                    <div className="flex items-center gap-3 mt-4 pt-3 border-t border-slate-800/80 text-slate-400">
                      {member.linkedin ? (
                        <a
                          href={member.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-[#f06600] transition-colors text-sm"
                          title="LinkedIn"
                        >
                          <BsLinkedin />
                        </a>
                      ) : null}
                      {member.twitter ? (
                        <a
                          href={member.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-[#f06600] transition-colors text-sm"
                          title="Twitter / X"
                        >
                          <BsTwitterX />
                        </a>
                      ) : null}
                      {!member.linkedin && !member.twitter && (
                        <span className="text-[11px] text-slate-500 font-medium">Beta Communes Team</span>
                      )}
                    </div>
                  </div>

                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 px-4 rounded-3xl bg-[#0d1424]/40 border border-slate-800/60 max-w-xl mx-auto">
              <div className="w-14 h-14 rounded-2xl bg-orange-500/10 text-[#f06600] mx-auto flex items-center justify-center text-2xl mb-4 border border-orange-500/20">
                <BsPeopleFill />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Our Team</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Team members will appear here once added from the admin panel.
              </p>
            </div>
          )}

        </div>
      </section>


      {/* =========================================================================
          8. KEY MILESTONES ALONG THE WAY (TIMELINE)
          ========================================================================= */}
      <section className="py-24 bg-white border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-4" data-aos="fade-up">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-extrabold text-[#f06600] mb-2">
                <span className="w-2 h-2 rounded-full bg-[#f06600] inline-block"></span>
                <span>OUR JOURNEY</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900">
                Key Milestones <br className="hidden sm:block" />
                <span className="text-[#f06600]">Along the Way</span>
              </h2>
            </div>
            <p className="text-slate-600 text-sm sm:text-base max-w-md font-normal leading-relaxed">
              A journey of continuous learning, innovation, and growth.
            </p>
          </div>

          {/* Desktop Horizontal Timeline (lg and above) */}
          <div className="hidden lg:block relative pt-4 pb-4">
            {/* Connecting Track Line */}
            <div className="timeline-track-line"></div>

            <div className="grid grid-cols-6 gap-8 relative z-10">
              {activeMilestones.map((item, idx) => (
                <div key={item.year || idx} className="milestone-node text-center" data-aos="fade-up" data-aos-delay={idx * 80}>
                  <div className="text-lg font-black text-slate-900 tracking-tight">{item.year}</div>
                  <div className="milestone-dot"></div>
                  <div className="font-bold text-xs text-slate-900 uppercase tracking-wide">{item.title}</div>
                  <p className="text-slate-500 text-xs mt-1 leading-normal">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile & Tablet Vertical Timeline (< lg: dots on one side linked with line, year & info on exact same axis) */}
          <div className="block lg:hidden relative pt-2 pb-2">
            {/* Continuous Vertical Connecting Line */}
            <div className="absolute left-[13px] sm:left-[17px] top-4 bottom-4 w-0.5 bg-gradient-to-b from-[#f06600] via-orange-400 to-orange-200"></div>

            <div className="space-y-6 sm:space-y-7 relative z-10">
              {activeMilestones.map((item, idx) => (
                <div
                  key={item.year || idx}
                  className="relative flex items-start pl-9 sm:pl-11 group"
                  data-aos="fade-up"
                  data-aos-delay={idx * 60}
                >
                  {/* Dot on the left side, precisely centered on the vertical line */}
                  <div className="absolute left-[6px] sm:left-[10px] top-2 w-3.5 h-3.5 rounded-full bg-[#f06600] border-2 border-white shadow-[0_0_0_2px_#f06600] z-10 transition-transform duration-300 group-hover:scale-125"></div>

                  {/* Year & Info on the exact same axis right next to the dot */}
                  <div className="flex-1 bg-white border border-slate-200/90 rounded-2xl p-4 shadow-sm group-hover:border-orange-200 group-hover:bg-orange-50/20 transition-all duration-300">
                    <div className="flex items-center gap-2.5 mb-1 flex-wrap">
                      <span className="text-base sm:text-lg font-black text-[#f06600] tracking-tight">
                        {item.year}
                      </span>
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-400"></span>
                      <h4 className="font-bold text-xs sm:text-sm text-slate-900 uppercase tracking-wide">
                        {item.title}
                      </h4>
                    </div>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>


      {/* =========================================================================
          9. CALL TO ACTION BANNER (Warm Orange Gradient)
          ========================================================================= */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="rounded-3xl p-8 sm:p-12 bg-gradient-to-r from-[#f06600] via-[#f77f00] to-[#ff9900] text-white shadow-2xl shadow-orange-500/25 flex flex-col lg:flex-row items-center justify-between gap-8"
            data-aos="zoom-in"
          >
            <div className="space-y-3 text-center lg:text-left">
              <div className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-orange-100 bg-black/15 px-3 py-1 rounded-full">
                <span>➔</span>
                <span>LET'S BUILD A BETTER TOMORROW</span>
              </div>
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white">
                Want to Be Part of Our Journey?
              </h2>
              <p className="text-orange-100 text-sm sm:text-base font-medium">
                Let's create something meaningful together.
              </p>
            </div>

            <Link
              to="/contactus"
              className="bg-white hover:bg-slate-50 text-slate-900 font-extrabold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2.5 text-sm sm:text-base cursor-pointer shrink-0"
            >
              <span>Get in Touch</span>
              <BsArrowRight className="text-base text-[#f06600]" />
            </Link>
          </div>
        </div>
      </section>


      {/* =========================================================================
          10. INTERACTIVE VIDEO MODAL
          ========================================================================= */}
      {videoModalOpen && (
        <div 
          className="video-modal-backdrop"
          onClick={() => setVideoModalOpen(false)}
        >
          <div 
            className="relative w-full max-w-4xl bg-slate-900 rounded-3xl overflow-hidden border border-slate-700 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-950">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#f06600]"></span>
                <span className="text-xs font-bold uppercase tracking-wider text-white">
                  Beta Communes · The Story
                </span>
              </div>
              <button 
                onClick={() => setVideoModalOpen(false)}
                className="text-slate-400 hover:text-white text-2xl cursor-pointer"
              >
                <BsX />
              </button>
            </div>

            {/* Video Player */}
            <div className="relative aspect-video bg-black flex items-center justify-center">
              <iframe
                className="w-full h-full"
                src="https://www.youtube-nocookie.com/embed/dQw4w9WgXcQ?autoplay=1"
                title="Beta Communes Showcase"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default About;